import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger } from '@fluentui/react-components'
import { LineItemGroupProps } from '../LineItemGroup/LineItemGroup'
import { AddRegular, DismissRegular } from '@fluentui/react-icons'
import { InvoiceMutationDto, LineItemType } from '../../../../../config/finance/invoiceConfig'
import { IObjectWithKey, ISelection, MarqueeSelection, Selection, SelectionZone } from '@fluentui/react'
import { useForceUpdate } from '@fluentui/react-hooks'
import { useFormikContext } from 'formik'
import LineItemSelector from '../LineItemSelector'
import { PricePrecedence } from '../../../../../config/finance/financeConfig'
import { LineItemEntity } from '../../../../../hooks/finance/invoice/useLineItemEntities'
import { useTranslation } from 'react-i18next'

type Props = LineItemGroupProps & {
    open: boolean
    setOpen: (open: boolean) => void
}
export default function LineItemDialog(props: Props): ReactElement {
    const forceUpdate = useForceUpdate()
    const { t: translate } = useTranslation()

    const { setFieldValue } = useFormikContext<InvoiceMutationDto>()
    const selection = useMemo<ISelection<LineItemEntity & IObjectWithKey>>(
        () =>
            new Selection({
                items: [...props.lineItemEntities.orphans.values()].flatMap(entities => entities),
                onSelectionChanged: forceUpdate,
                getKey: entity => entity.id,
            }),
        [props.lineItemEntities.orphans],
    )
    return (
        <Dialog
            open={props.open}
            onOpenChange={(event, data) => {
                props.setOpen(data.open)
            }}
        >
            <DialogSurface>
                <MarqueeSelection selection={selection}>
                    <SelectionZone selection={selection} toggleWithoutModifierPressed selectionPreservedOnEmptyClick>
                        <DialogBody>
                            <DialogTitle>{translate('invoice.edit.form.invoiceline.add')}</DialogTitle>
                            <DialogContent>
                                <LineItemSelector
                                    {...props}
                                    entities={props.lineItemEntities.orphans}
                                    selection={selection}
                                    findQuantity={props.lineItemEntities.findQuantity}
                                    getPrice={props.lineItemEntities.getPrice}
                                    showPrice={props.lineItemGroup.precedence === PricePrecedence.Inherited}
                                />
                            </DialogContent>
                            <DialogActions>
                                <DialogTrigger disableButtonEnhancement>
                                    <Button appearance="transparent" icon={<DismissRegular />}>
                                        {translate('action.cancel')}
                                    </Button>
                                </DialogTrigger>
                                <Button
                                    appearance="primary"
                                    icon={<AddRegular />}
                                    onClick={() => {
                                        setFieldValue(`lineItemGroups[${props.index}].lineItems`, [
                                            ...props.lineItemGroup.lineItems,
                                            ...selection
                                                .getSelection()
                                                .map(item => props.lineItemEntities.map(item))
                                                .filter(item => item.type !== LineItemType.FixedPrice)
                                                .filter(Boolean),
                                        ])
                                        setFieldValue(
                                            `lineItemGroups[${props.index}].fixedPriceId`,
                                            selection
                                                .getSelection()
                                                .map(item => props.lineItemEntities.map(item))
                                                .find(item => item.type === LineItemType.FixedPrice)?.entityId,
                                        )
                                        props.setOpen(false)
                                    }}
                                >
                                    {translate('action.add')}
                                </Button>
                            </DialogActions>
                        </DialogBody>
                    </SelectionZone>
                </MarqueeSelection>
            </DialogSurface>
        </Dialog>
    )
}
